import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { SubmissionSummaryCard } from '../../../maf/maf-submit-application/submission-summary-card/submission-summary-card';
import { AppButtons } from '../../../../shared/app-buttons/app-buttons';
import { MafContent, SubmissionSummaryCardData } from '../../../models/maf-submission-model';
import { DocumentPreviewModal } from '../../../../shared/document-preview-modal/document-preview-modal';
import { PopupModal } from '../../../../shared/popup-modal/popup-modal';
import { PopupButtonsConfig } from '../../../../shared/models/popup-buttons.model';
import { FilePickerService } from '../../../../core/services/file-picker.service';
import { ToasterService } from '../../../../shared/toaster/toaster.service';
import { Images } from '../../../../core/constants/image.constants';
import { ApiService } from '../../../../core/services/api.service';
import { ApplicationReviewService } from '../../../services/application-review.service';
import { ReviewQueueSharedService } from '../../../services/review-queue-shared.service';
import { SubmissionStatus } from '../../constants/application-review.model';
import { DateTimeConversionHelper } from '../../../../shared/helper/date-time-conversion.helper';
import { FileSizeConversionHelper } from '../../../../shared/helper/file-size-conversion.helper';
import { getFromStorage } from '../../../../core/constants/auth-state';
import { BulkUploadRequest, DeleteFileRequest, MafFileUpload, MafMember, UploadedFile } from '../../../../shared/maf/maf-file-upload/maf-file-upload';

@Component({
  selector: 'app-maf-app-review',
  standalone: true,
  imports: [CommonModule, SubmissionSummaryCard, DocumentPreviewModal, PopupModal, MatIconModule, AppButtons, MafFileUpload],
  templateUrl: './maf-app-review.html',
  styleUrl: './maf-app-review.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MafAppReview implements OnChanges {

  // Inputs

  @Input() appReviewData: any;
  @Input() selectedApplicant: {
    applicantId?: string;
    iconName: string;
    label: string;
    description: string;
  } | null = null;
  @Output() mafVerificationChanged = new EventEmitter<boolean>();
  @Output() mafSharedApplicantsVerificationUpdated = new EventEmitter<{ applicantIds: string[]; isVerified: boolean }>();
  @Input() isReadOnly = false;

  // Public State

  readonly images = Images;

  previewFile: MafContent | null = null;
  showPreviewModal = false;
  showDeleteModal = false;
  showOfflineMafUploadPopup = false;
  showMerUploadPopup = false;
  deleteModalTitle = '';
  itemToDelete: MafContent | null = null;
  cardTypeToDelete: 'maf' | 'mer' | null = null;
  readonly SubmissionStatus = SubmissionStatus;
  isMafPdfGenerated = false;
  isMafSubmitted = false;
  unsignedMafDocument: MafContent | null = null;
  offlineMafPopupMembers: MafMember[] = [];
  merPopupMembers: MafMember[] = [];
  offlinePopupCoversMultipleMembers = false;
  includedDependentNames: string[] = [];
  private lastOpenedOfflinePopupType: 'maf' | 'mer' | null = null;

  mafCard: SubmissionSummaryCardData = this.createEmptyMafCard();
  merCard: SubmissionSummaryCardData = this.createEmptyMerCard();

  private readonly reviewQueueSharedService = inject(ReviewQueueSharedService);


  deletePopupButtons: PopupButtonsConfig[] = [
    {
      layout: 'stacked',
      buttons: [
        { label: 'Delete', variant: 'primary', action: () => this.confirmDelete() },
        { label: 'Cancel', variant: 'secondary', action: () => this.closeDeleteModal() },
      ],
    },
  ];

  // Private State

  private readonly uploadTimers = new Set<ReturnType<typeof setInterval>>();
  private offlinePreviewObjectUrl: string | null = null;
  private reopenOfflineUploadPopupAfterPreview = false;
  private readonly toaster = inject(ToasterService);
  private readonly apiService = inject(ApiService);
  private readonly appReviewService = inject(ApplicationReviewService);

  // Constructor

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly filePickerService: FilePickerService,
    private readonly destroyRef: DestroyRef,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.destroyRef.onDestroy(() => {
      this.cleanupUploadTimers();
      this.clearOfflinePreviewObjectUrl();
      this.revokeCardLocalFileUrls(this.mafCard);
      this.revokeCardLocalFileUrls(this.merCard);
    });
  }

  // Lifecycle

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appReviewData'] || changes['selectedApplicant']) {
      this.initializeCardsFromApi();
    }
  }

  // Computed Getters

  get hasMafFile(): boolean {
    return this.getCardContent(this.mafCard).length > 0;
  }

  get hasMerFile(): boolean {
    return this.getCardContent(this.merCard).length > 0;
  }

  get hasUnsignedMafOnly(): boolean {
    return !!this.unsignedMafDocument && !this.hasMafFile;
  }

  get shouldShowNeedSignedMafSupportBanner(): boolean {
    return this.getMafCopyType() === 'Offline Copy' && !this.hasMafFile && !this.shouldShowCommonAcrossBadge;
  }

  get mafActionLabel(): string {
    if (this.hasMafFile) {
      return 'View / Edit Digital Form Data';
    }

    if (this.getMafCopyType() === 'Offline Copy') {
      return 'Download MAF Template (PDF)';
    }

    if (this.unsignedMafDocument) {
      return 'Preview MAF (Unsigned Copy)';
    }

    return '';
  }

  get mafActionIcon(): string {
    if (this.hasMafFile) {
      return 'quote-lead-summary';
    }

    if (this.getMafCopyType() === 'Offline Copy') {
      return 'icon-download';
    }

    if (this.unsignedMafDocument) {
      return 'icon-eye';
    }

    return 'upload';
  }

  get mafActionMeta(): string {
    const generatedDate = this.unsignedMafDocument?.tags?.[0];
    return this.hasUnsignedMafOnly && generatedDate ? `Generated on ${generatedDate}` : '';
  }

  get showMafAppReviewAction(): boolean {
    if (this.shouldShowCommonMafInfoForSelectedApplicant) {
      return false;
    }

    if (this.hasMafFile) {
      return this.getMafCopyType() === 'Online Copy';
    }
    return true;
  }

  get primaryApplicantId(): string {
    const submissions = this.appReviewData?.mafInfo?.mafUploadedDocuments?.submissions ?? [];
    for (const submission of submissions) {
      const primaryApplicant = (submission?.applicants ?? []).find(
        (applicant: any) => applicant?.applicantType === 'Primary Insured',
      );
      if (primaryApplicant?.applicantId) {
        return primaryApplicant.applicantId;
      }
    }

    return '';
  }

  get isPrimaryApplicantSelected(): boolean {
    return !!this.selectedApplicant?.applicantId && this.selectedApplicant.applicantId === this.primaryApplicantId;
  }

  get commonAcrossApplicantDependentIds(): string[] {
    const submissions = this.appReviewData?.mafInfo?.mafUploadedDocuments?.submissions ?? [];
    const dependentIds = new Set<string>();

    submissions.forEach((submission: any) => {
      (submission?.applicants ?? []).forEach((applicant: any) => {
        const applicantId = applicant?.applicantId;
        if (!applicantId || applicantId === this.primaryApplicantId) {
          return;
        }

        if (applicant?.mafShared) {
          dependentIds.add(applicantId);
        }
      });
    });

    return Array.from(dependentIds);
  }

  get hasAnyCommonAcrossApplicants(): boolean {
    return this.commonAcrossApplicantDependentIds.length > 0;
  }

  get sharedMafAffectedApplicantNames(): string[] {
    const submissions = this.appReviewData?.mafInfo?.mafUploadedDocuments?.submissions ?? [];
    const affectedApplicants = new Map<string, string>();

    submissions.forEach((submission: any) => {
      (submission?.applicants ?? []).forEach((applicant: any) => {
        const applicantId = applicant?.applicantId;
        const applicantName = applicant?.applicantName;
        if (!applicantId || !applicantName) {
          return;
        }

        if (applicant?.applicantType !== 'Primary Insured' && applicant?.mafShared) {
          affectedApplicants.set(applicantId, applicantName);
        }
      });
    });

    return Array.from(affectedApplicants.values());
  }

  get showSharedMafDeleteContent(): boolean {
    return this.cardTypeToDelete === 'maf' && this.isPrimaryApplicantSelected && this.hasAnyCommonAcrossApplicants;
  }

  get isSelectedApplicantCommonAcrossApplicants(): boolean {
    const selectedApplicantId = this.selectedApplicant?.applicantId ?? '';
    return !this.isPrimaryApplicantSelected && this.commonAcrossApplicantDependentIds.includes(selectedApplicantId);
  }

  get hasPrimarySignedMaf(): boolean {
    const primaryId = this.primaryApplicantId;
    if (!primaryId) {
      return false;
    }

    const submissions = this.appReviewData?.mafInfo?.mafUploadedDocuments?.submissions ?? [];
    return submissions.some((submission: any) => {
      const includesPrimaryApplicant = (submission?.applicants ?? []).some(
        (applicant: any) => applicant?.applicantId === primaryId,
      );
      if (!includesPrimaryApplicant) {
        return false;
      }

      return (submission?.documents ?? []).some(
        (document: any) => document?.documentType === 'UploadSignedMafForm',
      );
    });
  }

  get shouldShowCommonAcrossBadge(): boolean {
    if (this.getMafCopyType() !== 'Offline Copy' || !this.hasPrimarySignedMaf) {
      return false;
    }

    if (this.isPrimaryApplicantSelected) {
      return this.hasAnyCommonAcrossApplicants;
    }

    return this.isSelectedApplicantCommonAcrossApplicants;
  }

  get shouldShowCommonMafInfoForSelectedApplicant(): boolean {
    return this.shouldShowCommonAcrossBadge && this.isSelectedApplicantCommonAcrossApplicants;
  }

  get mafTopLeftBadge(): string {
    if (!this.shouldShowCommonAcrossBadge) {
      return '';
    }

    return 'Common Across Applicants';
  }

  get mafInfoPanelTitle(): string {
    if (!this.shouldShowCommonMafInfoForSelectedApplicant) {
      return '';
    }

    return 'Common MAF as Primary Applicant';
  }

  get mafInfoPanelText(): string {
    if (!this.shouldShowCommonMafInfoForSelectedApplicant) {
      return '';
    }

    return 'MAF is held under Primary Applicant\'s slot.';
  }

  // Public Action Handlers

  onDigitalFormAction(): void {
    if (this.isReadOnly) {
      return;
    }

    if (!this.hasMafFile) {
      if (this.getMafCopyType() === 'Offline Copy') {
        if (this.unsignedMafDocument) {
          this.onMafDownload(this.unsignedMafDocument);
        } else {
          this.fetchOfflineMafTemplate(true);
        }
        return;
      }

      if (this.unsignedMafDocument) {
        this.onMafView(this.unsignedMafDocument);
        return;
      }

      this.onMafUploadAction();
      return;
    }

    // Set selected applicant id before navigating
    const data = {
      ...this.appReviewData,
      selectedApplicantId: this.selectedApplicant?.applicantId,
    };
    this.reviewQueueSharedService.applicationReviewData.set(data);
    sessionStorage.setItem('applicationReviewData', JSON.stringify(data));

    this.router.navigate(['maf-online'], { relativeTo: this.route });
  }

  onMafUploadAction(): void {
    if (this.isReadOnly) {
      return;
    }

    if (this.getMafCopyType() === 'Offline Copy') {
      if (this.shouldShowCommonMafInfoForSelectedApplicant) {
        return;
      }

      this.openOfflineMafUploadPopup();
      return;
    }

    this.selectSingleReviewPdf('maf');
  }

  onOfflineMafUploadPopupClose(): void {
    this.showOfflineMafUploadPopup = false;
    this.cdr.markForCheck();
  }

  onOfflineMafBulkUploadRequested(request: BulkUploadRequest): void {
    if (this.isReadOnly) {
      return;
    }

    this.includedDependentNames = request.dependents
      .filter((dependent) => dependent.includedInPrimary)
      .map((dependent) => dependent.name);

    const allStagedFiles: Array<{ file: UploadedFile; member: MafMember }> = [];
    const collectStagedFiles = (member: MafMember): void => {
      (member.files ?? []).filter((file) => !file.done).forEach((file) => {
        allStagedFiles.push({ file, member });
      });
    };

    const { primaryMember, dependents } = request;
    collectStagedFiles(primaryMember);
    dependents.forEach((dependent) => collectStagedFiles(dependent));

    const inclusionOnlyDependents = dependents.filter(
      (dependent) => dependent.includedInPrimary && (dependent.files ?? []).filter((file) => !file.done).length === 0,
    );

    if (allStagedFiles.length === 0 && inclusionOnlyDependents.length === 0) {
      if (this.isSelectedApplicantCommonAcrossApplicants) {
        this.showOfflineMafUploadPopup = false;
        this.fetchApplicationReviewData();
        this.cdr.markForCheck();
        return;
      }

      this.toaster.show({
        message: 'Please select a signed MAF file before uploading.',
        status: 'error',
        showClose: true,
      });
      return;
    }

    const hasInclusionDependents = dependents.some((dependent) => dependent.includedInPrimary);
    const primaryStagedFiles = (primaryMember.files ?? []).filter((file) => !file.done);

    const missingSubmissionIds: string[] = [];
    if ((primaryStagedFiles.length > 0 || hasInclusionDependents) && !primaryMember.mafFormSubmissionId) {
      missingSubmissionIds.push('Primary member submission ID');
    }

    dependents.forEach((dependent) => {
      const dependentStagedFiles = (dependent.files ?? []).filter((file) => !file.done);
      const willBeIncluded = dependent.includedInPrimary || dependentStagedFiles.length > 0;
      if (willBeIncluded && !dependent.mafFormSubmissionId) {
        missingSubmissionIds.push(`${dependent.name} submission ID`);
      }
    });

    if (missingSubmissionIds.length > 0) {
      this.toaster.show({
        status: 'error',
        message: `Upload failed: missing submission IDs for ${missingSubmissionIds.join(', ')}.`,
        showClose: true,
      });
      return;
    }

    const formData = new FormData();
    const leadInfoId = this.resolveLeadInfoId();
    const finalSubmissionId = this.resolveFinalSubmissionId();

    if (!leadInfoId || !finalSubmissionId) {
      this.toaster.show({
        message: 'Unable to upload right now. Missing required review context. Please refresh and try again.',
        status: 'error',
        showClose: true,
      });
      return;
    }

    formData.append('LeadInfoId', leadInfoId);
    formData.append('FinalSubmissionId', finalSubmissionId);
    formData.append('Module', 'SignedMaf');
    formData.append('ProductId', this.resolveProductId());

    let submissionIndex = 0;

    if (primaryStagedFiles.length > 0 || hasInclusionDependents) {
      formData.append(`MafFormSubmissions[${submissionIndex}].mafFormSubmissionId`, primaryMember.mafFormSubmissionId ?? '');

      primaryStagedFiles.forEach((uploadedFile, fileIndex) => {
        formData.append(`MafFormSubmissions[${submissionIndex}].Files[${fileIndex}].formFiles`, uploadedFile.file, uploadedFile.file.name);
        formData.append(`MafFormSubmissions[${submissionIndex}].Files[${fileIndex}].documentInfoId`, uploadedFile.documentInfoId ?? '');
      });

      const primaryDoneFiles = (primaryMember.files ?? []).filter((file) => file.done);
      const primaryDoneOffset = primaryStagedFiles.length;
      primaryDoneFiles.forEach((uploadedFile, fileIndex) => {
        if (uploadedFile.documentInfoId) {
          formData.append(
            `MafFormSubmissions[${submissionIndex}].Files[${primaryDoneOffset + fileIndex}].documentInfoId`,
            uploadedFile.documentInfoId,
          );
        }
      });

      submissionIndex++;
    }

    dependents.filter((dependent) => !dependent.includedInPrimary).forEach((dependent) => {
      const dependentStagedFiles = (dependent.files ?? []).filter((file) => !file.done);
      if (dependentStagedFiles.length === 0) {
        return;
      }

      formData.append(`MafFormSubmissions[${submissionIndex}].mafFormSubmissionId`, dependent.mafFormSubmissionId ?? '');

      dependentStagedFiles.forEach((uploadedFile, fileIndex) => {
        formData.append(`MafFormSubmissions[${submissionIndex}].Files[${fileIndex}].formFiles`, uploadedFile.file, uploadedFile.file.name);
        formData.append(`MafFormSubmissions[${submissionIndex}].Files[${fileIndex}].documentInfoId`, uploadedFile.documentInfoId ?? '');
      });

      const dependentDoneFiles = (dependent.files ?? []).filter((file) => file.done);
      const dependentDoneOffset = dependentStagedFiles.length;
      dependentDoneFiles.forEach((uploadedFile, fileIndex) => {
        if (uploadedFile.documentInfoId) {
          formData.append(
            `MafFormSubmissions[${submissionIndex}].Files[${dependentDoneOffset + fileIndex}].documentInfoId`,
            uploadedFile.documentInfoId,
          );
        }
      });

      submissionIndex++;
    });

    dependents.filter((dependent) => dependent.includedInPrimary).forEach((dependent) => {
      const dependentStagedFiles = (dependent.files ?? []).filter((file) => !file.done);

      formData.append(`MafFormSubmissions[${submissionIndex}].mafFormSubmissionId`, dependent.mafFormSubmissionId ?? '');
      formData.append(`MafFormSubmissions[${submissionIndex}].sharedMafFormSubmissionId`, primaryMember.mafFormSubmissionId ?? '');

      dependentStagedFiles.forEach((uploadedFile, fileIndex) => {
        formData.append(`MafFormSubmissions[${submissionIndex}].Files[${fileIndex}].formFiles`, uploadedFile.file, uploadedFile.file.name);
        formData.append(`MafFormSubmissions[${submissionIndex}].Files[${fileIndex}].documentInfoId`, uploadedFile.documentInfoId ?? '');
      });

      submissionIndex++;
    });

    if (submissionIndex === 0) {
      return;
    }

    this.appReviewService
      .saveApplicationReviewUpload(formData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          allStagedFiles.forEach(({ file }) => {
            file.isUploading = false;
            file.done = true;
            file.progress = 0;
          });

          // Update UI immediately so tab verification is not delayed by the follow-up refresh call.
          this.mafCard = {
            ...this.mafCard,
            status: 'completed',
          };
          this.emitMafVerificationChanged();

          const sharedApplicantIds = dependents
            .filter((dependent) => dependent.includedInPrimary)
            .map((dependent) => dependent.id)
            .filter((applicantId): applicantId is string => !!applicantId);

          if (sharedApplicantIds.length > 0) {
            this.mafSharedApplicantsVerificationUpdated.emit({
              applicantIds: sharedApplicantIds,
              isVerified: true,
            });
          }

          this.showOfflineMafUploadPopup = false;
          this.toaster.show({
            message: response?.messages?.[0] ?? 'MAF uploaded successfully.',
            status: 'success',
            showClose: true,
          });
          this.fetchApplicationReviewData();
          this.cdr.markForCheck();
        },
        error: () => {
          allStagedFiles.forEach(({ file }) => {
            file.isUploading = false;
            file.progress = 0;
          });

          this.toaster.show({
            message: 'MAF upload failed.',
            status: 'error',
            showClose: true,
          });
          this.cdr.markForCheck();
        },
      });
  }

  private stopOfflineUploadProgressAnimation(files: UploadedFile[]): void {
    files.forEach((file) => {
      file.progress = 0;
    });
    this.cleanupUploadTimers();
  }

  onOfflineMafDeleteRequested(request: DeleteFileRequest): void {
    if (this.isReadOnly) {
      return;
    }

    const leadInfoId = this.resolveLeadInfoId(request.leadInfoId);
    if (!leadInfoId) {
      this.toaster.show({
        message: 'Unable to delete right now. Missing lead information. Please refresh and try again.',
        status: 'error',
        showClose: true,
      });
      return;
    }

    const payload = {
      leadInfoId,
      documentInfoId: request.documentInfoId,
      module: 'SignedMaf',
      isApplicationReview: true,
    };

    this.apiService.subscribeWithCleanup(
      this.apiService.deleteMafDocument(payload),
      () => {
        const member = this.offlineMafPopupMembers.find((item) => item.id === request.member.id);
        if (member?.files) {
          member.files.splice(request.fileIndex, 1);
        }
        this.toaster.show({
          message: 'Document deleted successfully.',
          status: 'success',
          showClose: true,
        });
        this.fetchApplicationReviewData();
        this.cdr.markForCheck();
      },
      this.destroyRef,
    );
  }

  onMerAction(): void {
    if (this.isReadOnly) {
      return;
    }

    this.openMerUploadPopup();
  }

  onMerUploadPopupClose(): void {
    this.showMerUploadPopup = false;
    this.cdr.markForCheck();
  }

  onMerBulkUploadRequested(request: BulkUploadRequest): void {
    if (this.isReadOnly) {
      return;
    }

    const allFiles = [...(request.primaryMember?.files ?? []), ...(request.dependents ?? []).flatMap((d) => d.files ?? [])];
    const stagedFile = allFiles.find((f) => !f.done);

    if (!stagedFile?.file) {
      this.toaster.show({
        message: 'Please select a MER file before uploading.',
        status: 'error',
        showClose: true,
      });
      return;
    }

    this.showMerUploadPopup = false;
    this.submitMerUpload(stagedFile.file);
  }

  onMerDeleteRequested(request: DeleteFileRequest): void {
    if (this.isReadOnly) {
      return;
    }

    const leadInfoId = this.resolveLeadInfoId(request.leadInfoId);
    if (!leadInfoId || !request.documentInfoId) {
      return;
    }

    this.apiService.subscribeWithCleanup(
      this.apiService.deleteMafDocument({
        leadInfoId,
        documentInfoId: request.documentInfoId,
        module: 'DocumentSubmission',
        isApplicationReview: true,
      }),
      () => {
        this.toaster.show({
          message: 'Document deleted successfully.',
          status: 'success',
          showClose: true,
        });
        this.fetchApplicationReviewData();
        this.openMerUploadPopup();
      },
      this.destroyRef,
    );
  }

  onMerPreviewRequested(file: UploadedFile): void {
    if (this.showMerUploadPopup) {
      this.reopenOfflineUploadPopupAfterPreview = true;
      this.showMerUploadPopup = false;
    }

    this.clearOfflinePreviewObjectUrl();

    const previewUrl = file.fileUrl || (file.file ? URL.createObjectURL(file.file) : null);
    if (!previewUrl) {
      this.toaster.show({
        message: 'Preview link is unavailable for this file.',
        status: 'error',
        showClose: true,
      });
      return;
    }

    if (!file.fileUrl) {
      this.offlinePreviewObjectUrl = previewUrl;
    }

    this.previewFile = {
      fileName: file.name,
      tags: ['MER', FileSizeConversionHelper.formatFileSize(file.size)],
      viewUrl: previewUrl,
      downloadUrl: file.fileUrl ?? previewUrl,
    };
    this.showPreviewModal = true;
    this.cdr.markForCheck();
  }

  onMafView(item: MafContent): void {
    if (!item?.viewUrl) {
      this.toaster.show({ message: 'Preview link is unavailable for this file.', status: 'error', showClose: true });
      return;
    }
    this.previewFile = { ...item, viewUrl: item.viewUrl };
    this.showPreviewModal = true;
    this.cdr.markForCheck();
  }

  onMafDownload(item: MafContent): void {
    const downloadUrl = item?.downloadUrl || item?.viewUrl;

    if (!downloadUrl) {
      this.toaster.show({ message: 'Download link is unavailable for this file.', status: 'error', showClose: true });
      return;
    }

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = item.fileName || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  onMafDelete(item: MafContent): void {
    if (this.isReadOnly) {
      return;
    }

    const mafContent = this.removeFileFromCard(this.mafCard, item);
    const isMaf = !!mafContent;

    this.itemToDelete = item;
    this.cardTypeToDelete = isMaf ? 'maf' : 'mer';
    this.deleteModalTitle = isMaf ? 'Delete Offline MAF?' : 'Delete MER?';
    this.showDeleteModal = true;
    this.cdr.markForCheck();
  }

  confirmDelete(): void {
    if (!this.itemToDelete || !this.cardTypeToDelete) return;

    const documentInfoId = this.itemToDelete.documentInfoId;

    if (!documentInfoId) {
      this.removeDocumentFromUi();
      return;
    }

    const payload = {
      leadInfoId: this.resolveLeadInfoId(),
      documentInfoId,
      module: this.cardTypeToDelete === 'maf' ? 'SignedMaf' : 'DocumentSubmission',
      isApplicationReview: true,
    };

    if (!payload.leadInfoId) {
      this.toaster.show({
        message: 'Unable to delete right now. Missing lead information. Please refresh and try again.',
        status: 'error',
        showClose: true,
      });
      return;
    }

    this.apiService.subscribeWithCleanup(
      this.apiService.deleteMafDocument(payload),
      () => this.removeDocumentFromUi(),
      this.destroyRef,
    );
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.itemToDelete = null;
    this.cardTypeToDelete = null;
    this.cdr.markForCheck();
  }

  closePreviewModal(): void {
    const shouldReopenOfflinePopup = this.reopenOfflineUploadPopupAfterPreview;
    this.reopenOfflineUploadPopupAfterPreview = false;
    this.clearOfflinePreviewObjectUrl();
    this.showPreviewModal = false;
    this.previewFile = null;

    if (shouldReopenOfflinePopup) {
      if (this.lastOpenedOfflinePopupType === 'mer') {
        this.showMerUploadPopup = true;
      } else {
        this.showOfflineMafUploadPopup = true;
      }
    }

    this.cdr.markForCheck();
  }

  onOfflineMafPreviewRequested(file: UploadedFile): void {
    if (this.showOfflineMafUploadPopup) {
      this.reopenOfflineUploadPopupAfterPreview = true;
      this.showOfflineMafUploadPopup = false;
    }

    this.clearOfflinePreviewObjectUrl();

    const previewUrl = file.fileUrl || (file.file ? URL.createObjectURL(file.file) : null);
    if (!previewUrl) {
      this.toaster.show({
        message: 'Preview link is unavailable for this file.',
        status: 'error',
        showClose: true,
      });
      return;
    }

    if (!file.fileUrl) {
      this.offlinePreviewObjectUrl = previewUrl;
    }

    this.previewFile = {
      fileName: file.name,
      tags: ['MAF', FileSizeConversionHelper.formatFileSize(file.size)],
      viewUrl: previewUrl,
      downloadUrl: file.fileUrl ?? previewUrl,
    };
    this.showPreviewModal = true;
    this.cdr.markForCheck();
  }

  onUploadCompleted(event: { file: File; type: 'maf' | 'mer' }): void {
    if (this.isReadOnly) {
      return;
    }

    const payload = event.type === 'maf' ? this.buildMafUploadPayload(event.file) : this.buildMerUploadPayload(event.file);

    this.appReviewService
      .saveApplicationReviewUpload(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.toaster.show({
            message: response?.messages?.[0] ?? `${event.type.toUpperCase()} uploaded successfully.`,
            status: 'success',
            showClose: true,
          });

          this.fetchApplicationReviewData();
        },

        error: () => {
          this.toaster.show({
            message: `${event.type.toUpperCase()} upload failed.`,
            status: 'error',
            showClose: true,
          });

          if (event.type === 'maf') {
            this.revokeCardLocalFileUrls(this.mafCard);
            this.mafCard = this.createEmptyMafCard();
          } else {
            this.revokeCardLocalFileUrls(this.merCard);
            this.merCard = this.createEmptyMerCard();
          }

          this.cdr.markForCheck();
        },
      });
  }

  onMafUploadSuccess(file: File): void {
    this.submitMafUpload(file);
  }

  onMerUploadSuccess(file: File): void {
    this.submitMerUpload(file);
  }

  // Card Initialization

  private initializeCardsFromApi(): void {
    this.mafCard = this.createEmptyMafCard();
    this.merCard = this.createEmptyMerCard();
    this.unsignedMafDocument = null;

    const submissionStatus = this.appReviewData?.mafInfo?.submissionStatus;
    this.isMafPdfGenerated = submissionStatus === SubmissionStatus.PdfGenerated;
    this.isMafSubmitted = submissionStatus === SubmissionStatus.ApplicationSubmitted;

    const signedMafDocument = this.getMafDocumentByType('UploadSignedMafForm');
    const unsignedMafDocument = this.getMafDocumentByType('DownloadMafForm');
    this.unsignedMafDocument = unsignedMafDocument ? this.mapMafDocumentToContent(unsignedMafDocument, 'Unsigned Copy') : null;

    if (signedMafDocument) {
      this.mafCard = {
        ...this.mafCard,
        status: 'completed',
        completedDate: signedMafDocument.createdOn,
        content: [this.mapMafDocumentToContent(signedMafDocument, this.getMafCopyType())],
      };
    } else if (this.shouldShowCommonMafInfoForSelectedApplicant) {
      const primarySignedMafDocument = this.getPrimarySignedMafDocument();
      this.mafCard = {
        ...this.mafCard,
        status: 'completed',
        completedDate: primarySignedMafDocument?.createdOn ?? '',
      };
    } else if (this.getMafCopyType() === 'Offline Copy') {
      this.fetchOfflineMafTemplate();
    }

    const merDocument = this.getMerDocument();

    if (merDocument) {
      this.merCard = {
        ...this.merCard,
        status: 'completed',
        completedDate: merDocument.createdOn,
        content: [
          {
            fileName: merDocument.fileName,
            documentInfoId: merDocument.documentId,
            tags: [DateTimeConversionHelper.toMonthDayYear(merDocument.createdOn), FileSizeConversionHelper.formatFileSize(merDocument.fileSize)],
            viewUrl: merDocument.viewUrl || merDocument.filePath,
            downloadUrl: merDocument.downloadUrl || merDocument.filePath,
          },
        ],
      };
    }

    this.emitMafVerificationChanged();

    this.cdr.markForCheck();
  }

  private openOfflineMafUploadPopup(): void {
    this.lastOpenedOfflinePopupType = 'maf';
    this.offlineMafPopupMembers = this.buildOfflineMafPopupMembers(this.isPrimaryApplicantSelected);
    this.offlinePopupCoversMultipleMembers = this.offlineMafPopupMembers.some(
      (member) => member.type === 'Family Member' && !!member.includedInPrimary,
    );
    this.showOfflineMafUploadPopup = true;
    this.cdr.markForCheck();
  }

  private openMerUploadPopup(): void {
    this.lastOpenedOfflinePopupType = 'mer';
    this.merPopupMembers = this.buildMerPopupMembers();
    this.showMerUploadPopup = true;
    this.cdr.markForCheck();
  }

  private buildMerPopupMembers(): MafMember[] {
    const applicantId = this.selectedApplicant?.applicantId ?? 'primary';
    const applicantName = this.selectedApplicant?.label ?? 'Applicant';
    const isPrimary = this.isPrimaryApplicantSelected;
    const applicantType: 'Primary Insured' | 'Family Member' = isPrimary ? 'Primary Insured' : 'Family Member';
    const icon = isPrimary ? 'icon-user-gradient' : 'self-family';

    const merDoc = this.getMerDocument();
    const mappedFiles: UploadedFile[] = merDoc
      ? [
        {
          file: new File([], merDoc.fileName ?? 'mer-form.pdf', { type: 'application/pdf' }),
          name: merDoc.fileName ?? 'mer-form.pdf',
          size: Number(merDoc.fileSize ?? 0),
          progress: 0,
          done: true,
          isUploading: false,
          documentInfoId: merDoc.documentId,
          fileUrl: merDoc.viewUrl || merDoc.downloadUrl || merDoc.filePath,
        },
      ]
      : [];

    return [
      {
        id: applicantId,
        name: applicantName,
        type: applicantType,
        icon,
        includedInPrimary: false,
        files: mappedFiles,
      },
    ];
  }

  private buildOfflineMafPopupMembers(includeAllMembersForPrimary: boolean): MafMember[] {
    const submissions = this.appReviewData?.mafInfo?.mafUploadedDocuments?.submissions ?? [];
    const membersById = new Map<string, MafMember>();

    submissions.forEach((submission: any) => {
      const submissionId = submission?.mafFormSubmissionId ?? '';
      const documents = (submission?.documents ?? []).filter(
        (document: any) => document?.documentType === 'UploadSignedMafForm',
      );

      (submission?.applicants ?? []).forEach((applicant: any, applicantIndex: number) => {
        const applicantId = applicant?.applicantId ?? `${submissionId || 'applicant'}-${applicantIndex}`;
        const applicantType = applicant?.applicantType === 'Primary Insured' ? 'Primary Insured' : 'Family Member';
        const mappedFiles: UploadedFile[] = documents.map((document: any) => ({
          file: new File([], document?.fileName ?? 'signed-maf.pdf', { type: 'application/pdf' }),
          name: document?.fileName ?? 'signed-maf.pdf',
          size: Number(document?.fileSize ?? 0),
          progress: 0,
          done: true,
          isUploading: false,
          documentInfoId: document?.documentId,
          mafFormSubmissionId: submissionId,
          fileUrl: document?.viewUrl || document?.downloadUrl || document?.filePath,
        }));

        membersById.set(applicantId, {
          id: applicantId,
          name: applicant?.applicantName || 'Applicant',
          type: applicantType,
          icon: applicantType === 'Primary Insured' ? 'icon-user-gradient' : 'self-family',
          includedInPrimary: applicantType === 'Family Member' ? !!applicant?.mafShared : false,
          mafFormSubmissionId: submissionId,
          files: mappedFiles,
        });
      });
    });

    const mappedMembers = Array.from(membersById.values());
    const primaryMember = mappedMembers.find((member) => member.id === this.primaryApplicantId)
      ?? mappedMembers.find((member) => member.type === 'Primary Insured');

    if (!includeAllMembersForPrimary) {
      const selectedApplicantId = this.selectedApplicant?.applicantId ?? '';
      const selectedMember = mappedMembers.find((member) => member.id === selectedApplicantId);
      if (selectedMember) {
        return [{
          ...selectedMember,
          includedInPrimary: false,
        }];
      }
    }

    if (primaryMember) {
      const dependentMembers = mappedMembers.filter((member) => member.id !== primaryMember.id);
      return [primaryMember, ...dependentMembers];
    }

    if (mappedMembers.length === 0 && this.selectedApplicant) {
      return [{
        id: this.selectedApplicant.applicantId ?? 'primary',
        name: this.selectedApplicant.label ?? 'Primary Applicant',
        type: 'Primary Insured',
        icon: 'icon-user-gradient',
        includedInPrimary: false,
        mafFormSubmissionId: '',
        files: [],
      }];
    }

    return mappedMembers;
  }

  private createEmptyMafCard(): SubmissionSummaryCardData {
    return {
      status: 'not-started',
      title: 'Medical Application Form (MAF)',
      completedDate: '',
      icon: 'icon-maf-document-file',
      type: 'maf',
      content: [],
    };
  }

  private createEmptyMerCard(): SubmissionSummaryCardData {
    return {
      status: 'not-started',
      title: 'Medical Examination Report (MER)',
      completedDate: '',
      icon: 'icon-maf-document-file',
      type: 'maf',
      content: [],
    };
  }

  // Document Fetch

  private getMafDocumentByType(documentType: 'DownloadMafForm' | 'UploadSignedMafForm'): any {
    const applicantId = this.selectedApplicant?.applicantId;

    if (!applicantId) return null;

    const submissions = this.appReviewData?.mafInfo?.mafUploadedDocuments?.submissions ?? [];

    const applicantSubmissions = submissions.filter(
      (submission: any) => submission?.applicants?.some((applicant: any) => applicant?.applicantId === applicantId),
    );

    for (const submission of applicantSubmissions) {
      const document = submission?.documents?.find((item: any) => item?.documentType === documentType);
      if (document) {
        return { ...document, versionNumber: submission?.versionNumber };
      }
    }

    return null;
  }

  private getPrimarySignedMafDocument(): any {
    const primaryId = this.primaryApplicantId;

    if (!primaryId) {
      return null;
    }

    const submissions = this.appReviewData?.mafInfo?.mafUploadedDocuments?.submissions ?? [];
    const primarySubmission = submissions.find((submission: any) =>
      (submission?.applicants ?? []).some((applicant: any) => applicant?.applicantId === primaryId),
    );

    if (!primarySubmission) {
      return null;
    }

    return (primarySubmission?.documents ?? []).find(
      (document: any) => document?.documentType === 'UploadSignedMafForm',
    ) ?? null;
  }

  private mapMafDocumentToContent(document: any, copyType: 'Online Copy' | 'Offline Copy' | 'Unsigned Copy'): MafContent {
    const version = document.versionNumber ? `v${document.versionNumber}` : 'v1.0';

    return {
      fileName: document.fileName,
      documentInfoId: document.documentId,
      version,
      tags: [DateTimeConversionHelper.toMonthDayYear(document.createdOn), FileSizeConversionHelper.formatFileSize(document.fileSize), copyType],
      viewUrl: document.viewUrl || document.filePath,
      downloadUrl: document.downloadUrl || document.filePath,
    };
  }

  private getMafCopyType(): 'Online Copy' | 'Offline Copy' {
    return this.appReviewData?.mafInfo?.isOnlineForm ? 'Online Copy' : 'Offline Copy';
  }

  private getMerDocument(): any {
    const applicantId = this.selectedApplicant?.applicantId;

    if (!applicantId) return null;

    return this.appReviewData?.merInfo?.find((item: any) => item?.applicantId === applicantId)?.merForm ?? null;
  }

  // Upload Flow

  private selectSingleReviewPdf(cardType: 'maf' | 'mer'): void {
    this.filePickerService.selectSinglePdf()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((file) => {
        if (!file) return;
        this.replaceCardWithSelectedFile(cardType, file);
      });
  }

  private replaceCardWithSelectedFile(cardType: 'maf' | 'mer', file: File): void {
    const card = this.getReviewCard(cardType);
    this.revokeCardLocalFileUrls(card);

    const fileUrl = URL.createObjectURL(file);

    this.setReviewCard(cardType, this.buildUpdatedCard(card, [
      this.buildSelectedFileContent(file, fileUrl, true, 10),
    ]));

    this.cdr.markForCheck();
    this.simulateUploadProgress(cardType, file);
  }

  private simulateUploadProgress(cardType: 'maf' | 'mer', file: File): void {
    const timerId = globalThis.setInterval(() => {
      const card = this.getReviewCard(cardType);
      const currentFile = this.getCardContent(card)[0];

      if (!currentFile?.isUploading) {
        this.clearUploadTimer(timerId);
        return;
      }

      const nextProgress = Math.min((currentFile.uploadProgress ?? 10) + 15, 100);
      const updatedFile: MafContent = { ...currentFile, uploadProgress: nextProgress, isUploading: nextProgress < 100 };

      if (nextProgress >= 100) {
        delete updatedFile.uploadProgress;
      }

      this.setReviewCard(cardType, this.buildUpdatedCard(card, [updatedFile]));
      this.cdr.markForCheck();

      if (nextProgress >= 100) {
        this.clearUploadTimer(timerId);
        this.onUploadCompleted({ file, type: cardType });
      }
    }, 200);

    this.uploadTimers.add(timerId);
  }

  private submitMafUpload(file: File): void {
    this.appReviewService.subscribeWithCleanup(
      this.appReviewService.saveApplicationReviewUpload(this.buildMafUploadPayload(file)),
      (response: any) => {
        this.toaster.show({
          message: response?.messages?.[0] ?? 'MAF uploaded successfully.',
          status: 'success',
          showClose: true,
        });
        this.fetchApplicationReviewData();
      },
      this.destroyRef,
    );
  }

  private submitMerUpload(file: File): void {
    this.appReviewService.subscribeWithCleanup(
      this.appReviewService.saveApplicationReviewUpload(this.buildMerUploadPayload(file)),
      (response: any) => {
        this.toaster.show({
          message: response?.messages?.[0] ?? 'MER uploaded successfully.',
          status: 'success',
          showClose: true,
        });
        this.fetchApplicationReviewData();
      },
      this.destroyRef,
    );
  }

  private fetchOfflineMafTemplate(downloadAfterLoad = false): void {
    const finalSubmissionId = this.resolveFinalSubmissionId();
    const leadInfoId = this.resolveLeadInfoId();
    if (!finalSubmissionId || !leadInfoId) return;

    this.apiService.subscribeWithCleanup(
      this.apiService.downloadOfflineMafForm({
        mafFinalSubmissionId: finalSubmissionId,
        leadInfoId,
        isApplicationReview: true,
      }),
      (response: any) => {
        if (response?.body?.mafTemplatePath) {
          this.unsignedMafDocument = {
            fileName: response.body.mafName || 'Medical Application Form (MAF)',
            viewUrl: response.body.mafTemplatePath,
            downloadUrl: response.body.mafTemplatePath,
            tags: [],
          };

          if (downloadAfterLoad && this.unsignedMafDocument) {
            this.onMafDownload(this.unsignedMafDocument);
          }

          this.cdr.markForCheck();
        }
      },
      this.destroyRef,
    );
  }

  private fetchApplicationReviewData(): void {
    const leadId = this.resolveLeadId();
    const productId = this.resolveProductId();
    const leadInfoId = this.resolveLeadInfoId();
    const selectedLead =
      this.reviewQueueSharedService.selectedLeadData() ??
      this.getPersistedApplicationReviewData() ?? {
        leadInfoId,
        id: leadInfoId,
        lead_Id: leadId,
        productId,
      };

    if (!leadId && !leadInfoId) return;

    this.appReviewService.subscribeWithCleanup(
      this.appReviewService.getApplicationReviewData(
        leadId || leadInfoId,
        productId,
        leadInfoId,
        selectedLead,
        true,
      ),
      (response) => {
        this.appReviewData = response?.body;
        if (this.appReviewData) {
          this.appReviewData.leadInfoId = leadInfoId || this.appReviewData.leadInfoId;
          this.appReviewData.leadId = leadId || this.appReviewData.leadId;
          this.appReviewData.productId = productId || this.appReviewData.productId;
          this.reviewQueueSharedService.setApplicationReviewData(this.appReviewData);
        }

        // rebuild cards from latest GET API response
        this.initializeCardsFromApi();

        this.cdr.markForCheck();
      },
      this.destroyRef,
    );
  }

  // Payload Builders

  private buildMafUploadPayload(file: File): FormData {
    const formData = new FormData();
    const leadInfoId = this.resolveLeadInfoId();
    const finalSubmissionId = this.resolveFinalSubmissionId();
    const selectedApplicantId = this.selectedApplicant?.applicantId;

    const mafSubmission = this.appReviewData?.mafInfo?.mafUploadedDocuments?.submissions?.find(
      (submission: any) => submission?.applicants?.some((applicant: any) => applicant?.applicantId === selectedApplicantId),
    );

    formData.append('LeadInfoId', leadInfoId);
    formData.append('FinalSubmissionId', finalSubmissionId);
    formData.append('Module', 'SignedMaf');
    formData.append('ProductId', this.resolveProductId());
    formData.append('MafFormSubmissions[0].MafFormSubmissionId', mafSubmission?.mafFormSubmissionId ?? '');
    formData.append('MafFormSubmissions[0].Files[0].formFiles', file);
    formData.append('MafFormSubmissions[0].Files[0].documentInfoId', '');

    return formData;
  }

  private buildMerUploadPayload(file: File): FormData {
    const formData = new FormData();
    const leadInfoId = this.resolveLeadInfoId();
    const finalSubmissionId = this.resolveFinalSubmissionId();
    const selectedApplicantId = this.selectedApplicant?.applicantId;

    formData.append('LeadInfoId', leadInfoId);
    formData.append('FinalSubmissionId', finalSubmissionId);
    formData.append('Module', 'DocumentSubmission');
    formData.append('Applicant[0].applicantId', selectedApplicantId ?? '');
    formData.append('Applicant[0].Documents[0].documentType', 'MerForm');
    formData.append('Applicant[0].Documents[0].file', file);

    return formData;
  }

  // Delete Flow

  private removeDocumentFromUi(): void {
    if (this.cardTypeToDelete === 'maf') {
      this.mafCard = this.createEmptyMafCard();
      this.emitMafVerificationChanged();

      if (this.isPrimaryApplicantSelected && this.hasAnyCommonAcrossApplicants) {
        this.mafSharedApplicantsVerificationUpdated.emit({
          applicantIds: this.commonAcrossApplicantDependentIds,
          isVerified: false,
        });
      }
    } else {
      this.merCard = this.createEmptyMerCard();
    }

    this.closeDeleteModal();
    this.toaster.show({ message: 'Document deleted successfully.', status: 'success', showClose: true });
    this.fetchApplicationReviewData();
    this.cdr.markForCheck();
  }

  // Card Helpers

  private getReviewCard(cardType: 'maf' | 'mer'): SubmissionSummaryCardData {
    return cardType === 'maf' ? this.mafCard : this.merCard;
  }

  private setReviewCard(cardType: 'maf' | 'mer', card: SubmissionSummaryCardData): void {
    if (cardType === 'maf') {
      this.mafCard = card;
      this.emitMafVerificationChanged();
    } else {
      this.merCard = card;
    }
  }

  private emitMafVerificationChanged(): void {
    const isMafVerifiedForSelectedApplicant = this.hasMafFile || this.shouldShowCommonMafInfoForSelectedApplicant;
    this.mafVerificationChanged.emit(isMafVerifiedForSelectedApplicant);
  }

  private buildUpdatedCard(card: SubmissionSummaryCardData, content: MafContent[]): SubmissionSummaryCardData {
    return { ...card, status: content.length ? 'completed' : 'not-started', content };
  }

  private buildSelectedFileContent(file: File, fileUrl: string, isUploading = false, uploadProgress?: number): MafContent {
    return {
      fileName: file.name,
      tags: [DateTimeConversionHelper.toMonthDayYear(new Date()), FileSizeConversionHelper.formatFileSize(file.size)],
      viewUrl: fileUrl,
      downloadUrl: fileUrl,
      originalFileName: file.name,
      sourceSize: file.size,
      isUploading,
      uploadProgress,
    };
  }

  private getCardContent(card: SubmissionSummaryCardData): MafContent[] {
    return (card?.content ?? []) as MafContent[];
  }

  private removeFileFromCard(card: SubmissionSummaryCardData, target: MafContent): MafContent[] | null {
    const files = this.getCardContent(card);
    const fileIndex = files.findIndex((file) => this.isMatchingFile(file, target));

    if (fileIndex < 0) return null;

    return files.filter((_, index) => index !== fileIndex);
  }

  private isMatchingFile(file: MafContent, target: MafContent): boolean {
    return (
      file === target ||
      (file.fileName === target.fileName &&
        (file.version ?? '') === (target.version ?? '') &&
        (file.downloadUrl ?? '') === (target.downloadUrl ?? ''))
    );
  }

  // URL & Timer Cleanup

  private revokeCardLocalFileUrls(card: SubmissionSummaryCardData): void {
    this.getCardContent(card).forEach((item) => this.revokeLocalFileUrl(item));
  }

  private revokeLocalFileUrl(item: MafContent): void {
    const localUrls = new Set(
      [item.viewUrl, item.downloadUrl].filter((url): url is string => url?.startsWith('blob:') ?? false),
    );
    localUrls.forEach((url) => URL.revokeObjectURL(url));
  }

  private clearUploadTimer(timerId: ReturnType<typeof setInterval>): void {
    globalThis.clearInterval(timerId);
    this.uploadTimers.delete(timerId);
  }

  private cleanupUploadTimers(): void {
    this.uploadTimers.forEach((timerId) => globalThis.clearInterval(timerId));
    this.uploadTimers.clear();
  }

  private clearOfflinePreviewObjectUrl(): void {
    if (!this.offlinePreviewObjectUrl) {
      return;
    }

    URL.revokeObjectURL(this.offlinePreviewObjectUrl);
    this.offlinePreviewObjectUrl = null;
  }

  private getPersistedApplicationReviewData(): any {
    const sharedData = this.reviewQueueSharedService.applicationReviewData();
    if (sharedData) {
      return sharedData;
    }

    const sessionData = sessionStorage.getItem('applicationReviewData');
    if (!sessionData) {
      return null;
    }

    try {
      return JSON.parse(sessionData);
    } catch {
      return null;
    }
  }

  private resolveLeadInfoId(fallback = ''): string {
    return this.appReviewData?.leadInfoId
      ?? this.getPersistedApplicationReviewData()?.leadInfoId
      ?? this.reviewQueueSharedService.selectedLeadData()?.leadInfoId
      ?? this.reviewQueueSharedService.selectedLeadData()?.id
      ?? getFromStorage('leadInfoId')
      ?? fallback
      ?? '';
  }

  private resolveFinalSubmissionId(): string {
    return this.appReviewData?.mafInfo?.mafFinalSubmissionId
      ?? this.getPersistedApplicationReviewData()?.mafInfo?.mafFinalSubmissionId
      ?? this.reviewQueueSharedService.selectedLeadData()?.mafFinalSubmissionId
      ?? '';
  }

  private resolveLeadId(): string {
    return this.appReviewData?.leadId
      ?? this.getPersistedApplicationReviewData()?.leadId
      ?? this.reviewQueueSharedService.selectedLeadData()?.lead_Id
      ?? this.reviewQueueSharedService.selectedLeadData()?.id
      ?? '';
  }

  private resolveProductId(): string {
    return this.appReviewData?.productId
      ?? this.getPersistedApplicationReviewData()?.productId
      ?? this.reviewQueueSharedService.selectedLeadData()?.productId
      ?? '';
  }
}
